import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { appConfig } from "@/config/env";
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  getTenantId,
  getIndustryId,
} from "@/services/tokenStorage";
import { refreshAccessToken } from "@/services/authService";
import { useAuthStore } from "@/store/authStore";

export const api = axios.create({
  baseURL: appConfig.apiBaseUrl,
  timeout: 30000,
});

function normalizeAccessToken(data: { access_token?: string; token?: string }): string | null {
  return data.access_token || data.token || null;
}

function forceSignOut(reason?: string): void {
  clearTokens();
  useAuthStore.getState().logout();
  if (typeof window === "undefined") return;
  const path = window.location.pathname || "";
  const loginPath = "/login";
  if (path === loginPath || path.endsWith("/login")) return;
  const from = encodeURIComponent(path + window.location.search);
  window.location.href = `${loginPath}?reason=${encodeURIComponent(reason || "session_expired")}&from=${from}`;
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const tenantId = getTenantId();
  if (tenantId) {
    config.headers["X-Tenant-ID"] = tenantId;
  }
  const industryId = getIndustryId();
  if (industryId) {
    config.headers["X-Industry-ID"] = industryId;
  }
  return config;
});

/**
 * True when a 401 came from a *module's* own vendor credential rather than from the
 * MarketIQ session.
 *
 * The auth edge rejects a bad session with a plain string detail ("Token is missing
 * tenant_id", "Invalid JWT signature", ...), while module failures carry a structured
 * `detail: {reason, message}` — for example YouTube's `youtube_token_rejected` when Google
 * refuses the stored channel token. Only a session failure may sign the user out; a vendor
 * failure is shown on the screen that asked for it.
 */
export function isDomainAuthFailure(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false;
  const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail;
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return false;
  return Boolean(String((detail as { reason?: unknown }).reason || "").trim());
}

let refreshPromise: Promise<string | null> | null = null;

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config;
    if (!original || error.response?.status !== 401) {
      return Promise.reject(error);
    }
    if (isDomainAuthFailure(error)) {
      // Not a session problem: leave the session alone and let the screen show the reason.
      return Promise.reject(error);
    }

    const detail = String(
      (error.response?.data as { detail?: string; error_description?: string } | undefined)?.detail ||
        (error.response?.data as { error_description?: string } | undefined)?.error_description ||
        "",
    );
    const missingAuth = /missing authorization/i.test(detail);

    const refresh = getRefreshToken();
    if (!refresh || (original as InternalAxiosRequestConfig & { _retriedRefresh?: boolean })._retriedRefresh) {
      forceSignOut(missingAuth ? "session_expired" : detail || "session_expired");
      return Promise.reject(error);
    }

    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken(refresh)
          .then((data) => {
            // No refresh endpoint yet: a null answer means "cannot refresh", not "reuse
            // the refresh token as an access token" (it has no tenant_id and the auth edge
            // would reject the retry).
            if (!data) return null;
            const access = normalizeAccessToken(data);
            if (!access) return null;
            setTokens(access, data.refresh_token || refresh);
            return access;
          })
          .finally(() => {
            refreshPromise = null;
          });
      }
      const next = await refreshPromise;
      if (!next) {
        forceSignOut("session_expired");
        return Promise.reject(error);
      }
      (original as InternalAxiosRequestConfig & { _retriedRefresh?: boolean })._retriedRefresh = true;
      original.headers.Authorization = `Bearer ${next}`;
      return api.request(original);
    } catch {
      forceSignOut("session_expired");
      return Promise.reject(error);
    }
  },
);

import { describeError, looksTechnical, type FriendlyError } from "@/lib/errorMessages";

/**
 * Keep raw technical detail for developers: it is logged to the console in dev
 * builds and never rendered to the user.
 */
function logTechnicalDetail(detail: string, context = "api"): void {
  if (!detail) return;
  const isDev = typeof import.meta !== "undefined" && Boolean(import.meta.env?.DEV);
  if (!isDev) return;
  // eslint-disable-next-line no-console
  console.debug(`[MarketIQ:${context}] technical detail (not shown to users):`, detail);
}

/**
 * Turn any thrown value into a friendly message + next action.
 * Technical detail is stripped from the message and logged instead.
 */
export function getFriendlyError(err: unknown, fallback = "Something went wrong"): FriendlyError {
  if (!axios.isAxiosError(err)) {
    const raw = err instanceof Error ? err.message : "";
    if (!raw) return { message: fallback };
    if (looksTechnical(raw)) {
      logTechnicalDetail(raw);
      return { message: fallback, technical: raw };
    }
    return { message: raw };
  }

  if (err.code === "ERR_NETWORK" || err.message.includes("Network Error")) {
    const base =
      appConfig.apiBaseUrl || (typeof window !== "undefined" ? window.location.origin : "");
    return {
      message: `Cannot reach MarketIQ backend at ${base}/api/marketing/auth/login`,
      nextAction: "Check that the backend is running, then try again.",
      reason: "network_error",
    };
  }
  if (err.code === "ECONNABORTED" || /timeout/i.test(err.message || "")) {
    return describeError({ reason: "timeout" });
  }

  const status = err.response?.status;
  const data = err.response?.data as
    | {
        detail?: unknown;
        message?: string;
        error_description?: string;
        reason?: string;
        next_action?: string;
        reference?: string;
        technical?: string;
        platform?: string;
      }
    | undefined;

  if (status === 401) {
    return {
      message: "Your session expired. Please sign in again.",
      nextAction: "Sign in again to continue.",
      reason: "permission_denied",
    };
  }

  const described = describeError(
    {
      status,
      reason: typeof data?.reason === "string" ? data.reason : undefined,
      message:
        typeof data?.message === "string"
          ? data.message
          : typeof data?.error_description === "string"
            ? data.error_description
            : undefined,
      nextAction: typeof data?.next_action === "string" ? data.next_action : undefined,
      reference: typeof data?.reference === "string" ? data.reference : undefined,
      detail: data?.detail,
      technical: typeof data?.technical === "string" ? data.technical : undefined,
      platform: typeof data?.platform === "string" ? data.platform : undefined,
    },
    fallback,
  );
  if (described.technical) logTechnicalDetail(described.technical);
  return described;
}

export function getErrorMessage(err: unknown, fallback = "Something went wrong"): string {
  return getFriendlyError(err, fallback).message;
}

